// ---------------------------------------------------------------------------
// A `PieClock` wired to a real Clock, and the only thing in the app that
// re-renders at frame rate.
//
// `PieClock` takes a fraction, deliberately: it is a shape, it is drawn in the
// gallery at a dozen fixed fractions, and it knows nothing about time. Something
// has to turn a Clock into that fraction sixty times a second, and where that
// something LIVES is the whole reason this file exists.
//
// It used to be the screen. Every play screen called `useClock` and passed
// `reading.remaining` down, so the frame loop's re-render was the screen's
// re-render: the board, the answer box, the words-tried list and the players
// strip all re-ran per frame, for the length of every round, to move one wedge
// by half a degree. React threw nearly all of it away — the output was identical
// — but the work was done, on a phone, on a battery, while somebody was trying
// to think of a word.
//
// So the subscription moved down here, to a component that holds the disc and
// nothing else. The screens keep `useClock`, which is now the four-times-a-
// second interval, and that is the right rate for everything they use a reading
// FOR: whole seconds, the urgent threshold at 20%, and whether the clock has
// stopped. This component keeps `useSmoothClock` and pays the frame cost over a
// subtree of about twenty divs.
//
// **`variant` and `label` stay props rather than being derived here**, even
// though this component has a reading of its own and could. They are not
// geometry. `LettersPlayScreen` turns the disc red on `urgent && !everyoneIn`,
// the conundrum has a `buzz` variant that has nothing to do with the clock, and
// the label is a sentence a screen reader hears — every one of those is the
// screen's judgement, and moving them here would be moving the decision away
// from the thing making it to save a prop.
// ---------------------------------------------------------------------------

import type { ReactElement } from 'react';
import type { Clock } from '@countdown/shared';
import { useSmoothClock } from '../../hooks/useClock.js';
import { PieClock } from './PieClock.js';
import type { ClockVariant } from './PieClock.js';

export interface LiveClockProps {
  /** The round's clock, or null when there is not one to draw. */
  clock: Clock | null;
  /**
   * A pause the CLOCK does not carry: a server pause, or a conundrum buzz held
   * by somebody else. A clock the server has already stopped carries its own
   * `pausedAt` and needs nothing here. See rule 2 in `useClock.ts`.
   */
  frozen?: boolean;
  /** Pixel diameter. See `PieClock` for the sizes the design draws. */
  size: number;
  variant?: ClockVariant;
  tickCount?: number;
  /** Accessible description. Required, because the disc has no numerals. */
  label: string;
  className?: string;
}

export function LiveClock({
  clock,
  frozen = false,
  size,
  variant,
  tickCount,
  label,
  className,
}: LiveClockProps): ReactElement {
  // The one frame-rate subscription in the app. Everything else reads the clock
  // through `useClock`, four times a second.
  const reading = useSmoothClock(clock, { frozen });

  return (
    <PieClock
      remaining={reading.remaining}
      size={size}
      variant={variant}
      tickCount={tickCount}
      label={label}
      className={className}
    />
  );
}
